import React from 'react';
import { MusicDistributionClientContext } from '../../context/MusicDistributionClientContext';
import MusicAlbumEditForm3EditPopupView from './MusicAlbumEditForm3EditPopupView';
import { MusicComposerEntity, MusicEntity, MusicFeaturingArtistEntity, MusicLyricWriterEntity, MusicPerformanceRoleEntity, MusicProductionAndEngineeringRoleEntity } from '../../types/MusicEntityTypes';
import { TunecoreReleaseTrackEntity } from '../../types/TunecoreReleaseEntityTypes';
import { MusicAlbumArtistEntity, MusicAlbumEntity } from '../../types/MusicAlbumEntityTypes';
import useCustomForm, { validateField } from '../../../common/hooks/CustomForm';
import ValidationUtility from '../../../common/utility/ValidationUtility';
import { FieldValidationErrorsType, showErrorFlush } from '../../../common/form/FrekulForm';


/**
 * 曲の編集ポップアップ
 */
export default function MusicAlbumEditForm3EditPopup({
  music,
  musicAlbum,
  onCancel,
  onDone,
  performanceRoleOptions,
  productionAndEngineeringRoleOptions,
} : {
  music: MusicEntity & {
    music_featuring_artists: MusicFeaturingArtistEntity[],
    music_lyric_writers: MusicLyricWriterEntity[],
    music_composers: MusicComposerEntity[],
    music_production_and_engineering_roles: MusicProductionAndEngineeringRoleEntity[],
    music_performance_roles: MusicPerformanceRoleEntity[],
    tunecore_release_track: TunecoreReleaseTrackEntity,
  },
  musicAlbum: MusicAlbumEntity & {
    music_album_artists: MusicAlbumArtistEntity[],
  }
  onDone: () => void,
  onCancel: () => void,
  performanceRoleOptions: { title: string, value: string }[],
  productionAndEngineeringRoleOptions: { title: string, value: string }[],
}) {

  type AjaxName = 'updateMusic';

  const [ajax, setAjax] = React.useState<AjaxName|null>(null);
  const { updateMusic } = React.useContext(MusicDistributionClientContext);

  const hasFeaturingArtists = (
    music.music_featuring_artists
    && music.music_featuring_artists.length > 0
  );
  const hasLyricWriters = (
    music.music_lyric_writers
    && music.music_lyric_writers.length > 0
    && music.music_lyric_writers[0].name.length > 0
  );
  const hasComposers = (
    music.music_composers
    && music.music_composers.length > 0
    && music.music_composers[0].name.length > 0
  );
  const hasProductionAndEngineeringRoles = (
    music.music_production_and_engineering_roles
    && music.music_production_and_engineering_roles.length > 0
    && music.music_production_and_engineering_roles[0].name.length > 0
  );
  const hasPerformanceRoles = (
    music.music_performance_roles
    && music.music_performance_roles.length > 0
    && music.music_performance_roles[0].name.length > 0
  );
  const firstArtistName = musicAlbum.music_album_artists[0].name;
  const firstLyricWriterName = hasLyricWriters
    ? music.music_lyric_writers[0].name : null;
  const firstComposerName = hasComposers
    ? music.music_composers[0].name : null;


  type MusicFieldName = 'title'|'title_kana'|'title_en'|
    'has_version'|'version_type'|'version'|'version_kana'|'version_en'|
    'remix_name'|'remix_name_kana'|'remix_name_en'|'remixer_name'|'remixer_name_en'|
    'place'|'place_kana'|'place_en'|'city'|'city_kana'|'city_en'|'year'|'year_kana'|'year_en'|
    'has_music_featuring_artists'|
    'music_same_lyric_writer'|
    'music_same_composer'|
    'is_inst'|'language'|'is_adult'|'isrc';
  type MusicValuesType = { [key in MusicFieldName]: string }
  type MusicErrorsType = { [key in MusicFieldName]?: FieldValidationErrorsType }
  
  const [
    musicValues,
    musicErrors,
    setMusicValues,
    setMusicErrors,
  ] = useCustomForm<MusicValuesType, MusicErrorsType, { name: MusicFieldName }>({
    defaultValue: {
      title: music.title || '',
      title_kana: music.title_kana || '',
      title_en: music.title_en || '',
      has_version: music.version ? '1' : '0',
      version_type: music.remixer_name ? 'remix' : (music.version ? 'other' : ''),
      version: music.version || '',
      version_kana: music.version_kana || '',
      version_en: music.version_en || '',
      remix_name: music.remixer_name ? music.version : '',
      remix_name_kana: music.remixer_name ? music.version_kana : '',
      remix_name_en: music.remixer_name ? music.version_en : '',
      remixer_name: music.remixer_name || '',
      remixer_name_en: music.remixer_name_en || '',
      place: '',
      place_kana: '',
      place_en: '',
      city: '',
      city_kana: '',
      city_en: '',
      year: '',
      year_kana: '',
      year_en: '',
      has_music_featuring_artists: hasFeaturingArtists ? '1' : '0',
      // eslint-disable-next-line no-nested-ternary
      music_same_lyric_writer: hasLyricWriters ? (firstLyricWriterName === firstArtistName ? '1' : '0') : '',
      // eslint-disable-next-zrline no-nested-ternary
      music_same_composer: hasComposers ? (firstComposerName === firstArtistName ? '1' : '0') : '',
      // eslint-disable-next-line no-nested-ternary
      is_inst: (music.is_inst === 2) ? '2' : (music.is_inst !== 0 ? '1' : '0'),
      language: music.language || '',
      // eslint-disable-next-line no-nested-ternary
      is_adult: (music.is_adult === null) ? '' : (music.is_adult !== 0 ? '1' : '0'),
      isrc: music.isrc || '',
    },
    validate(values, options, musicErrors): MusicErrorsType {
      const name = options.name;
      const value = values[name];
      const error = validateField([
        ['title_kana', 'isValidKana', 'ひらがなで入力してください。', ValidationUtility.isValidTextKana],
        ['title_en', 'isValidEn', '半角英数字で入力してください。', ValidationUtility.isValidTextEn],
        ['version_kana', 'isValidKana', 'ひらがなで入力してください。', ValidationUtility.isValidTextKana],
        ['version_en', 'isValidEn', '半角英数字で入力してください。', ValidationUtility.isValidTextEn],
        ['remix_name_kana', 'isValidKana', 'ひらがなで入力してください。', ValidationUtility.isValidTextKana],
        ['remix_name_en', 'isValidEn', '半角英数字で入力してください。', ValidationUtility.isValidTextEn],
        ['remixer_name_kana', 'isValidKana', 'ひらがなで入力してください。', ValidationUtility.isValidTextKana],
        ['remixer_name_en', 'isValidEn', '半角英数字で入力してください。', ValidationUtility.isValidTextEn],
        ['place_kana', 'isValidKana', 'ひらがなで入力してください。', ValidationUtility.isValidTextKana],
        ['place_en', 'isValidEn', '半角英数字で入力してください。', ValidationUtility.isValidTextEn],
        ['city_kana', 'isValidKana', 'ひらがなで入力してください。', ValidationUtility.isValidTextKana],
        ['city_en', 'isValidEn', '半角英数字で入力してください。', ValidationUtility.isValidTextEn],
        ['year_kana', 'isValidKana', 'ひらがなで入力してください。', ValidationUtility.isValidTextKana],
        ['year_en', 'isValidEn', '半角英数字で入力してください。', ValidationUtility.isValidTextEn],
        ['isrc', 'isValidEn', '半角英数字で入力してください。', ValidationUtility.isValidTextEn],
      ], name, value);
      return { ...musicErrors, [name]: error };
    },
  });

  type MusicFeaturingArtistFieldName = 'name'|'name_kana'|'name_en';
  type MusicFeaturingArtistValuesType = { [name in MusicFeaturingArtistFieldName]: string }[]
  type MusicFeaturingArtistErrorsType = { [name in MusicFeaturingArtistFieldName]?: FieldValidationErrorsType }[]

  const [
    musicFeaturingArtistValues,
    musicFeaturingArtistErrors,
    setMusicFeaturingArtistValues,
    setMusicFeaturingArtistErrors,
  ] = useCustomForm<
    MusicFeaturingArtistValuesType,
    MusicFeaturingArtistErrorsType,
    { type: 'set', index: number, name: MusicFeaturingArtistFieldName }|{ type: 'add' }
  >({
    defaultValue: hasFeaturingArtists ? music.music_featuring_artists : [{ name: '', name_en: '', name_kana: '' }],
    validate(valuesArray, options, musicFeaturingArtistErrors): MusicFeaturingArtistErrorsType {
      if (options.type === 'add') {
        return musicFeaturingArtistErrors || [];
      }
      const index = options.index;
      const name = options.name;
      const value = valuesArray[index][name];
      const error = validateField([
        ['name_kana', 'isValidKana', 'ひらがなで入力してください。', ValidationUtility.isValidTextKana],
        ['name_en', 'isValidKana', '半角英数字で入力してください。', ValidationUtility.isValidTextEn],
      ], name, value);
      const errorsArray = valuesArray.map((_, index) => musicFeaturingArtistErrors?.[index] || {});
      errorsArray[index] = { ...errorsArray[index], [name]: error };
      return errorsArray;
    },
  });

  type MusicLyricWriterFieldName = 'name'|'name_en';
  type MusicLyricWriterValuesType = { [name in MusicLyricWriterFieldName]: string }[]
  type MusicLyricWriterErrorsType = { [name in MusicLyricWriterFieldName]?: FieldValidationErrorsType }[]

  const [
    musicLyricWriterValues,
    musicLyricWriterErrors,
    setMusicLyricWriterValues,
    setMusicLyricWriterErrors,
  ] = useCustomForm<
    MusicLyricWriterValuesType,
    MusicLyricWriterErrorsType,
    { type: 'set', index: number, name: MusicLyricWriterFieldName }|{ type: 'add' }
  >({
    defaultValue: hasLyricWriters ? music.music_lyric_writers : [{ name: '', name_en: '' }],
    validate(valuesArray, options, musicLyricWriterErrors): MusicLyricWriterErrorsType {
      if (options.type === 'add') {
        return musicLyricWriterErrors || [];
      }
      const index = options.index;
      const name = options.name;
      const value = valuesArray[index][name];
      const error = validateField([
        ['name_en', 'isValidKana', '半角英数字で入力してください。', ValidationUtility.isValidTextEn],
      ], name, value);
      const errorsArray = valuesArray.map((_, index) => musicLyricWriterErrors?.[index] || {});
      errorsArray[index] = { ...errorsArray[index], [name]: error };
      return errorsArray;
    }
  });

  type MusicComposerFieldName = 'name'|'name_en';
  type MusicComposerValuesType = { [name in MusicComposerFieldName]: string }[]
  type MusicComposerErrorsType = { [name in MusicComposerFieldName]?: FieldValidationErrorsType }[]
  
  const [
    musicComposerValues,
    musicComposerErrors,
    setMusicComposerValues,
    setMusicComposerErrors,
  ] = useCustomForm<
    MusicComposerValuesType,
    MusicComposerErrorsType,
    { type: 'set', index: number, name: MusicComposerFieldName }|{ type: 'add' }
  >({
    defaultValue: hasComposers ? music.music_composers : [{ name: '', name_en: '' }],
    validate(valuesArray, options, musicComposerErrors): MusicComposerErrorsType {
      if (options.type === 'add') {
        return musicComposerErrors || [];
      }
      const index = options.index;
      const name = options.name;
      const value = valuesArray[index][name];
      const error = validateField([
        ['name_en', 'isValidKana', '半角英数字で入力してください。', ValidationUtility.isValidTextEn],
      ], name, value);
      const errorsArray = valuesArray.map((_, index) => musicComposerErrors?.[index] || {});
      errorsArray[index] = { ...errorsArray[index], [name]: error };
      return errorsArray;
    }
  });

  type MusicProductionAndEngineeringRoleFieldName = 'role'|'name'|'name_en';
  type MusicProductionAndEngineeringRoleValuesType = { [name in MusicProductionAndEngineeringRoleFieldName]: string }[];
  type MusicProductionAndEngineeringRoleErrorsType = { [name in MusicProductionAndEngineeringRoleFieldName]?: FieldValidationErrorsType }[];

  const [
    musicProductionAndEngineeringRoleValues,
    musicProductionAndEngineeringRoleErrors,
    setMusicProductionAndEngineeringRoleValues,
    setMusicProductionAndEngineeringRoleErrors,
  ] = useCustomForm<
    MusicProductionAndEngineeringRoleValuesType,
    MusicProductionAndEngineeringRoleErrorsType,
    { type: 'set', index: number, name: MusicProductionAndEngineeringRoleFieldName }|{ type: 'add' }
  >({
    defaultValue: hasProductionAndEngineeringRoles
      ? music.music_production_and_engineering_roles.map((musicProductionAndEngineeringRole) => {
        return {
          role: `${musicProductionAndEngineeringRole.role}`,
          name: musicProductionAndEngineeringRole.name,
          name_en: musicProductionAndEngineeringRole.name_en,
        };
      })
      : [{ role: '', name: '', name_en: '' }],
    validate(valuesArray, options, musicProductionAndEngineeringRoleErrors): MusicProductionAndEngineeringRoleErrorsType {
      if (options.type === 'add') {
        return musicProductionAndEngineeringRoleErrors || [];
      }
      const index = options.index;
      const name = options.name;
      const value = valuesArray[index][name];
      const error = validateField([
        ['name_en', 'isValidKana', '半角英数字で入力してください。', ValidationUtility.isValidTextEn],
      ], name, value);
      const errorsArray = valuesArray.map((_, index) => musicProductionAndEngineeringRoleErrors?.[index] || {});
      errorsArray[index] = { ...errorsArray[index], [name]: error };
      return errorsArray;
    },
  });

  type MusicPerformanceRoleFieldName = 'role'|'name'|'name_en';
  type MusicPerformanceRoleValuesType = { [name in MusicPerformanceRoleFieldName]: string }[];
  type MusicPerformanceRoleErrorsType = { [name in MusicPerformanceRoleFieldName]?: FieldValidationErrorsType }[];

  const [
    musicPerformanceRoleValues,
    musicPerformanceRoleErrors,
    setMusicPerformanceRoleValues,
    setMusicPerformanceRoleErrors,
  ] = useCustomForm<
    MusicPerformanceRoleValuesType,
    MusicPerformanceRoleErrorsType,
    { type: 'set', index: number, name: MusicPerformanceRoleFieldName }|{ type: 'add' }
  >({
    defaultValue: hasPerformanceRoles
      ? music.music_performance_roles.map((musicPerformanceRole) => {
        return {
          role: `${musicPerformanceRole.role}`,
          name: musicPerformanceRole.name,
          name_en: musicPerformanceRole.name_en,
        };
      })
      : [{ role: '', name: '', name_en: '' }],
    validate(valuesArray, options, musicPerformanceRoleErrors): MusicPerformanceRoleErrorsType {
      if (options.type === 'add') {
        return musicPerformanceRoleErrors || [];
      }
      const index = options.index;
      const name = options.name;
      const value = valuesArray[index][name];
      const error = validateField([
        ['name_en', 'isValidKana', '半角英数字で入力してください。', ValidationUtility.isValidTextEn],
      ], name, value);
      const errorsArray = valuesArray.map((_, index) => musicPerformanceRoleErrors?.[index] || {});
      errorsArray[index] = { ...errorsArray[index], [name]: error };
      return errorsArray;
    },
  });

  React.useEffect(() => {
    if (ajax === 'updateMusic') {
      updateMusic(music.id, {
        ...musicValues,
        music_featuring_artists: musicFeaturingArtistValues,
        music_lyric_writers: musicLyricWriterValues,
        music_composers: musicComposerValues,
        music_production_and_engineering_roles: musicProductionAndEngineeringRoleValues,
        music_performance_roles: musicPerformanceRoleValues,
      }).then((responseData) => {
        setAjax(null);
        setMusicErrors(responseData.validationErrors as { [key: string]: FieldValidationErrorsType });
        setMusicFeaturingArtistErrors(responseData.validationErrors?.music_featuring_artists as { [key: string]: FieldValidationErrorsType }[])
        setMusicLyricWriterErrors(responseData.validationErrors?.music_lyric_writers as { [key: string]: FieldValidationErrorsType }[]);
        setMusicComposerErrors(responseData.validationErrors?.music_composers as { [key: string]: FieldValidationErrorsType }[]);
        setMusicProductionAndEngineeringRoleErrors(responseData.validationErrors?.music_production_and_engineering_roles as { [key: string]: FieldValidationErrorsType }[]);
        setMusicPerformanceRoleErrors(responseData.validationErrors?.music_performance_roles as { [key: string]: FieldValidationErrorsType }[]);
        if (responseData.result) {
          onDone();
        } else {
          showErrorFlush('入力内容に不備があります。');
        }
      });
    }
  }, [ajax]);

  // eslint-disable-next-line max-len
  const isIsrcFetchedFromTunecore = !!(music.tunecore_release_track && music.tunecore_release_track.isrc);

  return (
    <MusicAlbumEditForm3EditPopupView
      getValue={(name) => {
        if (typeof name === 'string') {
          return musicValues[name];
        } else if (name[0] === 'music_featuring_artists') {
          return musicFeaturingArtistValues[name[1]][name[2]];
        } else if (name[0] === 'music_lyric_writers') {
          return musicLyricWriterValues[name[1]][name[2]];
        } else if (name[0] === 'music_composers') {
          return musicComposerValues[name[1]][name[2]];
        } else if (name[0] === 'music_production_and_engineering_roles') {
          return musicProductionAndEngineeringRoleValues[name[1]][name[2]];
        } else if (name[0] === 'music_performance_roles') {
          return musicPerformanceRoleValues[name[1]][name[2]];
        } else {
          throw new Error();
        }
      }}
      setValue={(name, value) => {
        if (typeof name === 'string') {
          setMusicValues({ ...musicValues, [name]: value }, { name });
        } else if (name[0] === 'music_featuring_artists') {
          const newMusicFeaturingArtistValues = [...musicFeaturingArtistValues];
          newMusicFeaturingArtistValues[name[1] as number] = { ...musicFeaturingArtistValues[name[1] as number], [name[2]]: value };
          setMusicFeaturingArtistValues(newMusicFeaturingArtistValues, { type: 'set', index: name[1] as number, name: name[2] as 'name'|'name_kana'|'name_en' });
        } else if (name[0] === 'music_lyric_writers') {
          const newMusicLyricWriterValues = [...musicLyricWriterValues];
          newMusicLyricWriterValues[name[1] as number] = { ...musicLyricWriterValues[name[1] as number], [name[2]]: value };
          setMusicLyricWriterValues(newMusicLyricWriterValues, { type: 'set', index: name[1] as number, name: name[2] as 'name'|'name_en' });
        } else if (name[0] === 'music_composers') {
          const newMusicComposerValues = [...musicComposerValues];
          newMusicComposerValues[name[1] as number] = { ...musicComposerValues[name[1] as number], [name[2]]: value };
          setMusicComposerValues(newMusicComposerValues, { type: 'set', index: name[1] as number, name: name[2] as 'name'|'name_en' });
        } else if (name[0] === 'music_production_and_engineering_roles') {
          const newMusicProductionAndEngineeringRoleValues = [...musicProductionAndEngineeringRoleValues];
          newMusicProductionAndEngineeringRoleValues[name[1] as number] = { ...musicProductionAndEngineeringRoleValues[name[1] as number], [name[2]]: value };
          setMusicProductionAndEngineeringRoleValues(newMusicProductionAndEngineeringRoleValues, { type: 'set', index: name[1] as number, name: name[2] as 'role'|'name'|'name_en' });
        } else if (name[0] === 'music_performance_roles') {
          const newMusicPerformanceRoleValues = [...musicPerformanceRoleValues];
          newMusicPerformanceRoleValues[name[1] as number] = { ...musicPerformanceRoleValues[name[1] as number], [name[2]]: value };
          setMusicPerformanceRoleValues(newMusicPerformanceRoleValues, { type: 'set', index: name[1] as number, name: name[2] as 'role'|'name'|'name_en' });
        } else {
          throw new Error();
        }
      }}
      getErrors={(name) => {
        if (typeof name === 'string') {
          return musicErrors?.[name] || {};
        } else if (name[0] === 'music_featuring_artists') {
          return musicFeaturingArtistErrors?.[name[1]]?.[name[2]] || {};
        } else if (name[0] === 'music_lyric_writers') {
          return musicLyricWriterErrors?.[name[1]]?.[name[2]] || {};
        } else if (name[0] === 'music_composers') {
          return musicComposerErrors?.[name[1]]?.[name[2]] || {};
        } else if (name[0] === 'music_production_and_engineering_roles') {
          return musicProductionAndEngineeringRoleErrors?.[name[1]]?.[name[2]] || {};
        } else if (name[0] === 'music_performance_roles') {
          return musicPerformanceRoleErrors?.[name[1]]?.[name[2]] || {};
        } else {
          throw new Error();
        }
      }}
      getNumberOfItems={(name) => {
        if (name === 'music_featuring_artists') {
          return musicFeaturingArtistValues.length;
        } else if (name === 'music_lyric_writers') {
          return musicLyricWriterValues.length;
        } else if (name === 'music_composers') {
          return musicComposerValues.length;
        } else if (name === 'music_production_and_engineering_roles') {
          return musicProductionAndEngineeringRoleValues.length;
        } else if (name === 'music_performance_roles') {
          return musicPerformanceRoleValues.length;
        } else {
          throw new Error();
        }
      }}
      addItem={(name, self) => {
        if (name === 'music_featuring_artists') {
          setMusicFeaturingArtistValues([...musicFeaturingArtistValues, { name: '', name_kana: '', name_en: ''}], { type: 'add' });
        } else if (name === 'music_lyric_writers') {
          if (self) {
            const firstMusicAlbumArtist = musicAlbum.music_album_artists[0];
            setMusicLyricWriterValues([{ name: firstMusicAlbumArtist.name, name_en: firstMusicAlbumArtist.name_en }], { type: 'add' });
          } else {
            setMusicLyricWriterValues([...musicLyricWriterValues, { name: '', name_en: '' }], { type: 'add' });
          }
        } else if (name === 'music_composers') {
          if (self) {
            const firstMusicAlbumArtist = musicAlbum.music_album_artists[0];
            setMusicComposerValues([{ name: firstMusicAlbumArtist.name, name_en: firstMusicAlbumArtist.name_en }], { type: 'add' });
          } else {
            setMusicComposerValues([...musicComposerValues, { name: '', name_en: '' }], { type: 'add' });
          }
        } else if (name === 'music_production_and_engineering_roles') {
          setMusicProductionAndEngineeringRoleValues([...musicProductionAndEngineeringRoleValues, { role: '', name: '', name_en: '' }], { type: 'add' });
        } else if (name === 'music_performance_roles') {
          setMusicPerformanceRoleValues([...musicPerformanceRoleValues, { role: '', name: '', name_en: '' }], { type: 'add' });
        } else {
          throw new Error();
        }
      }}
      artistName={musicAlbum.music_album_artists[0].name}
      isIsrcFetchedFromTunecore={isIsrcFetchedFromTunecore}
      onSave={() => setAjax('updateMusic')}
      onCancel={() => onCancel()}
      productionAndEngineeringRoleOptions={productionAndEngineeringRoleOptions}
      performanceRoleOptions={performanceRoleOptions}
    />
  );
}
